import type { BuiltFormDto } from '@core/api/workspace/models/builtFormDto';
import type { BuiltFormFieldDto } from '@core/api/workspace/models/builtFormFieldDto';
import type { FormThemeShadesDto } from '@core/api/workspace/models/formThemeShadesDto';
import type { BoundValueDto } from '@core/api/workspace/models/boundValueDto';
import {
  parseMultiChoiceDefault,
  readMultiChoiceDefaults,
  serializeMultiChoiceDefault,
  type BoundValue,
  type BuiltForm,
  DividerOpacity,
  DividerStyle,
  DividerThickness,
  type FieldConfig,
  FieldType,
  FormField,
  type FormFieldDataLibraryCollectionRef,
  FormInputAppearance,
  FormPage,
  FormThemeShades,
  type NumberGridConfig,
  type NumberGridItem,
  HeadingAlignment,
  HeadingFontFamily,
  HeadingFontStyle,
  HeadingFontWeight,
  HeadingLevel,
  ParagraphAlignment,
  ParagraphFontFamily,
  ParagraphFontStyle,
  ParagraphFontWeight,
  ParagraphLevel,
  type SpacerSize,
} from '../models/built-form';
import { createDefaultPage, DEFAULT_PRIMARY_LABEL, sortedPages } from './form-pages';
import { resolveThemeShades } from './form-theme-catalog';

/**
 * Backend field-type allowlist does not include `rating` yet
 * (`Unsupported field type: rating`). Persist as `number` and mark via
 * validation.pattern so load can restore the rating UI.
 */
const RATING_WIRE_TYPE = 'number';
const RATING_WIRE_MARKER = 'formops:rating';
const MULTI_CHOICE_GRID_WIRE_TYPE = 'multi_choice';
const MULTI_CHOICE_GRID_WIRE_MARKER = 'formops:multi_choice_grid';

/** Legacy wire: Choice Grid was saved as checkbox_group + this pattern marker. */
const LEGACY_SINGLE_CHOICE_GRID_WIRE_TYPE = 'checkbox_group';
const LEGACY_SINGLE_CHOICE_GRID_MARKER = 'formops:choice_grid';

const NUMBER_GRID_WIRE_TYPE = 'number';
const NUMBER_GRID_WIRE_MARKER = 'formops:number_grid';

const FIELD_TYPES = new Set<FieldType>([
  'heading',
  'paragraph',
  'divider',
  'spacer',
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'currency',
  'url',
  'checkbox',
  'checkbox_group',
  'radio_group',
  'switch',
  'select',
  'searchable_picker',
  'single_choice',
  'multi_choice',
  'date',
  'time',
  'datetime',
  'date_range',
  'time_range',
  'slider',
  'rating',
  'range_slider',
  'color',
  'numeric_stepper',
  'image_picker',
  'camera',
  'live_geo_coordinates',
  'address_selector',
  'map',
  'file_uploader',
  'audio_recorder',
  'video_recorder',
  'rich_text',
  'document_scanner',
  'drawing',
  'signature',
  'captcha',
  'multi_choice_grid',
  'single_choice_grid',
  'number_grid'
]);

const INPUT_APPEARANCES = new Set<FormInputAppearance>([
  'fill',
  'outline',
  'ghost',
  'floating',
  'ifta',
  'floatingOutline',
]);

const TEXT_ALIGNMENTS = new Set<HeadingAlignment>(['left', 'center', 'right']);
const TEXT_FONT_STYLES = new Set<HeadingFontStyle>(['normal', 'italic']);
const TEXT_FONT_FAMILIES = new Set<HeadingFontFamily>([
  'Inter',
  'Arial',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Courier New',
  'Comic Sans MS',
]);
const TEXT_FONT_WEIGHTS = new Set<HeadingFontWeight>([200, 400, 500, 600, 700]);
const DIVIDER_STYLES = new Set<DividerStyle>(['solid', 'dashed', 'dotted']);
const DIVIDER_THICKNESSES = new Set<DividerThickness>([1, 2, 3, 4]);
const DIVIDER_OPACITIES = new Set<DividerOpacity>([25, 50, 75, 100]);

function toFieldType(type: string): FieldType {
  const normalized = type.trim().toLowerCase() as FieldType;
  return FIELD_TYPES.has(normalized) ? normalized : 'text';
}

function toInputAppearance(value: BuiltFormDto['inputAppearance']): FormInputAppearance {
  return INPUT_APPEARANCES.has(value as FormInputAppearance)
    ? (value as FormInputAppearance)
    : 'fill';
}

function toTextLevel(value: number | undefined): HeadingLevel | undefined {
  if (value === 2 || value === 3 || value === 4 || value === 5) {
    return value;
  }
  return undefined;
}

function toTextAlignment(value: string | undefined): HeadingAlignment | undefined {
  if (value != null && TEXT_ALIGNMENTS.has(value as HeadingAlignment)) {
    return value as HeadingAlignment;
  }
  return undefined;
}

function toTextFontStyle(value: string | undefined): HeadingFontStyle | undefined {
  if (value != null && TEXT_FONT_STYLES.has(value as HeadingFontStyle)) {
    return value as HeadingFontStyle;
  }
  return undefined;
}

function toTextFontFamily(value: string | undefined): HeadingFontFamily | undefined {
  if (value != null && TEXT_FONT_FAMILIES.has(value as HeadingFontFamily)) {
    return value as HeadingFontFamily;
  }
  return undefined;
}

function toTextFontWeight(value: number | undefined): HeadingFontWeight | undefined {
  if (value != null && TEXT_FONT_WEIGHTS.has(value as HeadingFontWeight)) {
    return value as HeadingFontWeight;
  }
  return undefined;
}

function toParagraphLevel(value: number | undefined): ParagraphLevel | undefined {
  return toTextLevel(value);
}

function toParagraphAlignment(value: string | undefined): ParagraphAlignment | undefined {
  return toTextAlignment(value);
}

function toParagraphFontStyle(value: string | undefined): ParagraphFontStyle | undefined {
  return toTextFontStyle(value);
}

function toParagraphFontFamily(value: string | undefined): ParagraphFontFamily | undefined {
  return toTextFontFamily(value);
}

function toParagraphFontWeight(value: number | undefined): ParagraphFontWeight | undefined {
  return toTextFontWeight(value);
}

function toDividerStyle(value: string | undefined): DividerStyle | undefined {
  if (value != null && DIVIDER_STYLES.has(value as DividerStyle)) {
    return value as DividerStyle;
  }
  return undefined;
}

function toDividerThickness(value: number | undefined): DividerThickness | undefined {
  if (value != null && DIVIDER_THICKNESSES.has(value as DividerThickness)) {
    return value as DividerThickness;
  }
  return undefined;
}

function toDividerOpacity(value: number | undefined): DividerOpacity | undefined {
  if (value != null && DIVIDER_OPACITIES.has(value as DividerOpacity)) {
    return value as DividerOpacity;
  }
  return undefined;
}

function toSpacerSize(value: BuiltFormFieldDto['spacerSize']): SpacerSize | undefined {
  if (value === 'sm' || value === 'md' || value === 'lg' || value === 'xl') {
    return value;
  }
  return undefined;
}

function toDtoSpacerSize(
  value: SpacerSize | undefined,
): BuiltFormFieldDto['spacerSize'] | undefined {
  if (value === 'sm' || value === 'md' || value === 'lg') {
    return value;
  }
  if (value === 'xl') {
    return 'lg';
  }
  return undefined;
}

function toThemeColor(value: BuiltFormDto['themeColor']): FormThemeShades {
  if (!value) {
    return resolveThemeShades(undefined);
  }
  return resolveThemeShades({
    50: value['50'],
    100: value['100'],
    200: value['200'],
    300: value['300'],
    400: value['400'],
    500: value['500'],
    600: value['600'],
    700: value['700'],
    800: value['800'],
    900: value['900'],
    950: value['950'],
  });
}

function toThemeColorDto(value: FormThemeShades | undefined): FormThemeShadesDto | undefined {
  if (!value) {
    return undefined;
  }
  const shades = resolveThemeShades(value);
  return {
    '50': shades[50],
    '100': shades[100],
    '200': shades[200],
    '300': shades[300],
    '400': shades[400],
    '500': shades[500],
    '600': shades[600],
    '700': shades[700],
    '800': shades[800],
    '900': shades[900],
    '950': shades[950],
  };
}

function boundValueFromDto(field: BuiltFormFieldDto): BoundValue | undefined {
  const bound = field.default;
  if (bound?.source === 'variable' && bound.dataLibraryId?.trim()) {
    return {
      source: 'variable',
      dataLibraryId: bound.dataLibraryId.trim(),
      ...(bound.value !== undefined ? { value: bound.value } : {}),
    };
  }
  if (bound?.source === 'system' && bound.systemKey?.trim()) {
    return {
      source: 'system',
      systemKey: bound.systemKey.trim(),
      key: bound.systemKey.trim(),
      ...(bound.value !== undefined ? { value: bound.value } : {}),
    };
  }
  if (bound?.source === 'static' && bound.value != null && bound.value !== '') {
    return {
      source: 'static',
      value: bound.value,
    };
  }
  if (field.defaultValue != null && field.defaultValue !== '') {
    return {
      source: 'static',
      value: field.defaultValue,
    };
  }
  return undefined;
}

function boundValueToDto(bound: BoundValue | undefined): {
  default?: BoundValueDto;
  defaultValue?: string;
} {
  if (!bound) {
    return {};
  }
  if (bound.source === 'variable') {
    return {
      default: {
        source: 'variable',
        dataLibraryId: bound.dataLibraryId,
      },
    };
  }
  if (bound.source === 'system') {
    return {
      default: {
        source: 'system',
        systemKey: bound.systemKey as BoundValueDto['systemKey'],
      },
    };
  }
  if (bound.value === undefined || bound.value === null || bound.value === '') {
    return {};
  }
  return {
    default: {
      source: 'static',
      value: bound.value,
    },
    defaultValue: typeof bound.value === 'string' ? bound.value : String(bound.value),
  };
}

type BuiltFormFieldPersistDto = BuiltFormFieldDto & {
  /** Shown but not editable. Persisted with the field definition. */
  disabled?: boolean;
  /** Field-specific settings (rating scale, currency defaults, etc.). */
  config?: FieldConfig;
};

function mapDtoFieldToFormField(field: BuiltFormFieldPersistDto): FormField {
  const next: FormField = {
    id: field.id,
    type: toFieldType(field.type),
    label: field.label,
  };

  if (field.placeholder != null) {
    next.placeholder = field.placeholder;
  }
  if (typeof field.required === 'boolean') {
    next.required = field.required;
  }
  if (field.disabled === true) {
    next.disabled = true;
  }
  if (field.options) {
    next.options = [...field.options];
  }
  const collectionId = field.dataLibraryCollection?.dataLibraryId?.trim();
  if (collectionId) {
    const displayFieldKey = field.dataLibraryCollection?.displayFieldKey?.trim() || undefined;
    const secondaryDisplayFieldKey =
      field.dataLibraryCollection?.secondaryDisplayFieldKey?.trim() || undefined;
    const searchFields = (field.dataLibraryCollection?.searchFields ?? [])
      .map((key) => key.trim())
      .filter(Boolean);
    const pageSize = field.dataLibraryCollection?.pageSize;
    next.dataLibraryCollection = {
      dataLibraryId: collectionId,
      ...(displayFieldKey ? { displayFieldKey } : {}),
      ...(secondaryDisplayFieldKey ? { secondaryDisplayFieldKey } : {}),
      ...(searchFields.length > 0 ? { searchFields } : {}),
      ...(typeof pageSize === 'number' && pageSize > 0 ? { pageSize } : {}),
    };
    next.options = undefined;
  }
  if (field.helpText != null) {
    next.helpText = field.helpText;
  }

  if (field.config && typeof field.config === 'object') {
    next.config = { ...field.config };
  }

  const boundDefault = boundValueFromDto(field);
  if (boundDefault) {
    next.default = boundDefault;
    if (boundDefault.source === 'static') {
      const isMultiChoiceGridWire =
        next.type === 'multi_choice' &&
        field.validation?.pattern === MULTI_CHOICE_GRID_WIRE_MARKER;

      if (next.type === 'multi_choice' && !isMultiChoiceGridWire) {
        const values = parseMultiChoiceDefault(boundDefault.value);
        const serialized = serializeMultiChoiceDefault(values);
        next.config = values.length > 0 ? { defaultValue: values } : undefined;
        next.default = serialized ? { source: 'static', value: serialized } : undefined;
      } else if (typeof boundDefault.value === 'string') {
        next.config = { ...next.config, defaultValue: boundDefault.value };
      } else if (typeof boundDefault.value === 'number') {
        next.config = { ...next.config, defaultValue: boundDefault.value };
      } else if (typeof boundDefault.value === 'boolean') {
        next.config = { ...next.config, defaultValue: boundDefault.value };
      }
    }
  }

  if (field.validation) {
    next.validation = { ...field.validation };
  }

  const headingLevel = toTextLevel(field.headingLevel);
  if (headingLevel != null) {
    next.headingLevel = headingLevel;
  }

  const headingAlignment = toTextAlignment(field.headingAlignment);
  if (headingAlignment != null) {
    next.headingAlignment = headingAlignment;
  }

  const headingFontStyle = toTextFontStyle(field.headingFontStyle);
  if (headingFontStyle != null) {
    next.headingFontStyle = headingFontStyle;
  }

  const headingFontFamily = toTextFontFamily(field.headingFontFamily);
  if (headingFontFamily != null) {
    next.headingFontFamily = headingFontFamily;
  }

  if (field.headingColor != null && field.headingColor.trim()) {
    next.headingColor = field.headingColor.trim();
  }

  const headingFontWeight = toTextFontWeight(field.headingFontWeight);
  if (headingFontWeight != null) {
    next.headingFontWeight = headingFontWeight;
  }

  const paragraphLevel = toParagraphLevel(field.paragraphLevel);
  if (paragraphLevel != null) {
    next.paragraphLevel = paragraphLevel;
  }

  const paragraphAlignment = toParagraphAlignment(field.paragraphAlignment);
  if (paragraphAlignment != null) {
    next.paragraphAlignment = paragraphAlignment;
  }

  const paragraphFontStyle = toParagraphFontStyle(field.paragraphFontStyle);
  if (paragraphFontStyle != null) {
    next.paragraphFontStyle = paragraphFontStyle;
  }

  const paragraphFontFamily = toParagraphFontFamily(field.paragraphFontFamily);
  if (paragraphFontFamily != null) {
    next.paragraphFontFamily = paragraphFontFamily;
  }

  if (field.paragraphColor != null && field.paragraphColor.trim()) {
    next.paragraphColor = field.paragraphColor.trim();
  }

  const paragraphFontWeight = toParagraphFontWeight(field.paragraphFontWeight);
  if (paragraphFontWeight != null) {
    next.paragraphFontWeight = paragraphFontWeight;
  }

  const dividerStyle = toDividerStyle(field.dividerStyle);
  if (dividerStyle != null) {
    next.dividerStyle = dividerStyle;
  }

  const dividerThickness = toDividerThickness(field.dividerThickness);
  if (dividerThickness != null) {
    next.dividerThickness = dividerThickness;
  }

  if (field.dividerColor != null && field.dividerColor.trim()) {
    next.dividerColor = field.dividerColor.trim();
  }

  const dividerOpacity = toDividerOpacity(field.dividerOpacity);
  if (dividerOpacity != null) {
    next.dividerOpacity = dividerOpacity;
  }

  const spacerSize = toSpacerSize(field.spacerSize);
  if (spacerSize != null) {
    next.spacerSize = spacerSize;
  }
  return hydrateNumberGridFieldFromWire(
    hydrateLegacySingleChoiceGridField(
      hydrateMultiChoiceGridFieldFromWire(
        hydrateRatingFieldFromWire(next),
      ),
    ),
  );

}

/** Restore `single_choice_grid` from legacy checkbox_group + formops:choice_grid marker. */
function hydrateLegacySingleChoiceGridField(field: FormField): FormField {
  if (
    field.type !== LEGACY_SINGLE_CHOICE_GRID_WIRE_TYPE ||
    field.validation?.pattern !== LEGACY_SINGLE_CHOICE_GRID_MARKER
  ) {
    return field;
  }

  const { pattern, ...validationRest } = field.validation;
  void pattern;

  return {
    ...field,
    type: 'single_choice_grid',
    validation: validationRest,
  };
}

function hydrateMultiChoiceGridFieldFromWire(
  field: FormField,
): FormField {
  if (
    field.type !== MULTI_CHOICE_GRID_WIRE_TYPE ||
    field.validation?.pattern !== MULTI_CHOICE_GRID_WIRE_MARKER
  ) {
    return field;
  }

  const { pattern, ...validationRest } = field.validation;
  void pattern;

  return {
    ...field,
    type: 'multi_choice_grid',
    validation: validationRest,
  };
}

/** Restore client `rating` type from the wire encoding used in {@link mapFormFieldToDto}. */
function hydrateRatingFieldFromWire(field: FormField): FormField {
  if (field.type !== RATING_WIRE_TYPE) {
    return field;
  }
  if (field.validation?.pattern !== RATING_WIRE_MARKER) {
    return field;
  }

  const max = field.validation.max === 10 ? 10 : 5;
  const { pattern, ...validationRest } = field.validation;
  void pattern;

  return {
    ...field,
    type: 'rating',
    config: {
      ...field.config,
      max,
    },
    validation: {
      ...validationRest,
      min: 1,
      max,
    },
  };
}

function ratingScaleFromField(field: FormField): 5 | 10 {
  const configMax =
    field.config && 'max' in field.config && (field.config.max === 5 || field.config.max === 10)
      ? field.config.max
      : undefined;
  if (configMax != null) {
    return configMax;
  }
  if (field.validation?.max === 10) {
    return 10;
  }
  return 5;
}

function resolveFieldBoundDefault(field: FormField): BoundValue | undefined {
  if (field.type === 'multi_choice') {
    const serialized = serializeMultiChoiceDefault(readMultiChoiceDefaults(field));
    return serialized ? { source: 'static', value: serialized } : undefined;
  }
  if (field.default) {
    return field.default;
  }
  const legacy = field.config?.defaultValue;
  if (Array.isArray(legacy)) {
    const serialized = serializeMultiChoiceDefault(legacy);
    return serialized ? { source: 'static', value: serialized } : undefined;
  }
  if (legacy === undefined || legacy === null || legacy === '') {
    return undefined;
  }
  return { source: 'static', value: legacy };
}

function normalizeNumberGridColumns(raw: unknown): Array<{ id: string; label: string }> {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: Array<{ id: string; label: string }> = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) {
      continue;
    }
    const obj = item as { id?: unknown; key?: unknown; label?: unknown };
    const label = typeof obj.label === 'string' ? obj.label : '';
    const id =
      typeof obj.id === 'string' && obj.id.trim()
        ? obj.id.trim()
        : typeof obj.key === 'string' && obj.key.trim()
          ? obj.key.trim()
          : '';
    if (!id && !label.trim()) {
      continue;
    }
    out.push({ id: id || label.trim(), label });
  }
  return out;
}

function hydrateNumberGridFieldFromWire(
  field: FormField,
): FormField {
  const fromLegacyWire =
    field.type === NUMBER_GRID_WIRE_TYPE &&
    field.validation?.pattern === NUMBER_GRID_WIRE_MARKER;

  if (field.type !== 'number_grid' && !fromLegacyWire) {
    return field;
  }

  let next: FormField = field;
  if (fromLegacyWire) {
    const { pattern, ...validationRest } = field.validation!;
    void pattern;
    next = {
      ...field,
      type: 'number_grid',
      validation:
        Object.keys(validationRest).length > 0 ? validationRest : undefined,
    };
  }

  const config = (next.config ?? {}) as Record<string, unknown>;
  const columns = normalizeNumberGridColumns(config['columns']);
  const itemsDataLibraryCollection =
    (config['itemsDataLibraryCollection'] as
      | FormFieldDataLibraryCollectionRef
      | undefined) ??
    (next.dataLibraryCollection?.dataLibraryId
      ? next.dataLibraryCollection
      : undefined);

  const nextConfig: NumberGridConfig = {
    ...(Array.isArray(config['items'])
      ? { items: config['items'] as NumberGridItem[] }
      : {}),
    ...(typeof config['defaultValue'] === 'string'
      ? { defaultValue: config['defaultValue'] }
      : {}),
    ...(columns.length > 0 ? { columns } : {}),
    ...(itemsDataLibraryCollection ? { itemsDataLibraryCollection } : {}),
  };

  return {
    ...next,
    dataLibraryCollection: undefined,
    config: nextConfig,
  };
}



function mapFormFieldToDto(
  field: FormField,
): BuiltFormFieldPersistDto {
  const boundDto = boundValueToDto(resolveFieldBoundDefault(field));

  const isRating = field.type === 'rating';
  const isNumberGrid = field.type === 'number_grid';

  const wireType = isRating ? RATING_WIRE_TYPE : field.type;

  const ratingMax = isRating
    ? ratingScaleFromField(field)
    : undefined;

  // Strip legacy grid wire markers if still present.
  const validationWithoutGridMarker = (() => {
    if (
      (field.type === 'multi_choice_grid' &&
        field.validation?.pattern === MULTI_CHOICE_GRID_WIRE_MARKER) ||
      (field.type === 'number_grid' &&
        field.validation?.pattern === NUMBER_GRID_WIRE_MARKER)
    ) {
      const { pattern, ...rest } = field.validation!;
      void pattern;
      return Object.keys(rest).length > 0 ? rest : undefined;
    }

    return field.validation;
  })();

  // Build validation based on field type.
  const validation = isRating
      ? {
        ...(typeof field.required === 'boolean'
          ? { required: field.required }
          : field.validation?.required != null
            ? { required: field.validation.required }
            : {}),
        ...(field.validation ?? {}),
        min: 1,
        max: ratingMax!,
        integerOnly: true,
        pattern: RATING_WIRE_MARKER,
      }
      : validationWithoutGridMarker
        ? { ...validationWithoutGridMarker }
        : undefined;

  const numberGridConfig = isNumberGrid
    ? (() => {
        const config = (field.config ?? {}) as Record<string, unknown>;
        const columns = normalizeNumberGridColumns(config['columns']);
        return {
          ...config,
          ...(columns.length > 0 ? { columns } : {}),
        };
      })()
    : null;

  const dto: BuiltFormFieldPersistDto = {
    id: field.id,
    type: wireType,
    label: field.label,

    ...(field.placeholder != null
      ? { placeholder: field.placeholder }
      : {}),

    ...(typeof field.required === 'boolean'
      ? { required: field.required }
      : {}),

    ...(field.disabled === true
      ? { disabled: true }
      : {}),

    ...(field.dataLibraryCollection?.dataLibraryId
      ? {
        dataLibraryCollection: {
          dataLibraryId: field.dataLibraryCollection.dataLibraryId,

          ...(field.dataLibraryCollection.displayFieldKey?.trim()
            ? {
              displayFieldKey:
                field.dataLibraryCollection.displayFieldKey.trim(),
            }
            : {}),

          ...(field.dataLibraryCollection.secondaryDisplayFieldKey?.trim()
            ? {
              secondaryDisplayFieldKey:
                field.dataLibraryCollection.secondaryDisplayFieldKey.trim(),
            }
            : {}),

          ...((field.dataLibraryCollection.searchFields ?? [])
            .map((key) => key.trim())
            .filter(Boolean).length > 0
            ? {
              searchFields: (
                field.dataLibraryCollection.searchFields ?? []
              )
                .map((key) => key.trim())
                .filter(Boolean),
            }
            : {}),

          ...(typeof field.dataLibraryCollection.pageSize === 'number' &&
          field.dataLibraryCollection.pageSize > 0
            ? {
              pageSize: field.dataLibraryCollection.pageSize,
            }
            : {}),
        },
      }
      : field.options
        ? { options: [...field.options] }
        : {}),

    ...(field.helpText != null
      ? { helpText: field.helpText }
      : {}),

    ...(boundDto.default
      ? { default: boundDto.default }
      : {}),

    ...(boundDto.defaultValue !== undefined
      ? { defaultValue: boundDto.defaultValue }
      : {}),

    ...(validation
      ? { validation }
      : {}),

    ...(field.headingLevel != null
      ? { headingLevel: field.headingLevel }
      : {}),

    ...(field.headingAlignment != null
      ? { headingAlignment: field.headingAlignment }
      : {}),

    ...(field.headingFontStyle != null
      ? { headingFontStyle: field.headingFontStyle }
      : {}),

    ...(field.headingFontFamily != null
      ? { headingFontFamily: field.headingFontFamily }
      : {}),

    ...(field.headingColor != null
      ? { headingColor: field.headingColor }
      : {}),

    ...(field.headingFontWeight != null
      ? { headingFontWeight: field.headingFontWeight }
      : {}),

    ...(field.paragraphLevel != null
      ? { paragraphLevel: field.paragraphLevel }
      : {}),

    ...(field.paragraphAlignment != null
      ? { paragraphAlignment: field.paragraphAlignment }
      : {}),

    ...(field.paragraphFontStyle != null
      ? { paragraphFontStyle: field.paragraphFontStyle }
      : {}),

    ...(field.paragraphFontFamily != null
      ? { paragraphFontFamily: field.paragraphFontFamily }
      : {}),

    ...(field.paragraphColor != null
      ? { paragraphColor: field.paragraphColor }
      : {}),

    ...(field.paragraphFontWeight != null
      ? { paragraphFontWeight: field.paragraphFontWeight }
      : {}),

    ...(field.dividerStyle != null
      ? { dividerStyle: field.dividerStyle }
      : {}),

    ...(field.dividerThickness != null
      ? { dividerThickness: field.dividerThickness }
      : {}),

    ...(field.dividerColor != null
      ? { dividerColor: field.dividerColor }
      : {}),

    ...(field.dividerOpacity != null
      ? { dividerOpacity: field.dividerOpacity }
      : {}),

    ...(toDtoSpacerSize(field.spacerSize) != null
      ? { spacerSize: toDtoSpacerSize(field.spacerSize)! }
      : {}),

    ...(numberGridConfig
      ? { config: numberGridConfig }
      : field.config
      ? { config: { ...field.config } }
      : {}),
  };

  return dto;
}

export function mapBuiltFormDtoToBuiltForm(dto: BuiltFormDto): BuiltForm {
  const pages: FormPage[] = dto.pages?.length
    ? sortedPages(
        dto.pages.map((page) => ({
          id: page.id,
          order: page.order,
          ...(page.title ? { title: page.title } : {}),
          ...(page.description ? { description: page.description } : {}),
          cta: {
            primaryLabel: page.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL,
            ...(page.cta?.backLabel?.trim() ? { backLabel: page.cta.backLabel.trim() } : {}),
          },
          fields: (page.fields ?? []).map(mapDtoFieldToFormField),
        })),
      )
    : [
        createDefaultPage({
          cta: {
            primaryLabel: dto.submitLabel?.trim() || DEFAULT_PRIMARY_LABEL,
          },
          fields: (dto.fields ?? []).map(mapDtoFieldToFormField),
        }),
      ];

  return {
    title: dto.title,
    ...(dto.description ? { description: dto.description } : {}),
    inputAppearance: toInputAppearance(dto.inputAppearance),
    ...(dto.coverImageUrl ? { coverImageUrl: dto.coverImageUrl } : {}),
    themeColor: toThemeColor(dto.themeColor),
    pages,
  };
}

export function mapBuiltFormToDto(form: BuiltForm): BuiltFormDto {
  const inputAppearance = toInputAppearance(form.inputAppearance);
  const themeColor = toThemeColorDto(form.themeColor);
  const pages = sortedPages(form.pages?.length ? form.pages : [createDefaultPage()]);
  const lastPage = pages[pages.length - 1];
  const submitLabel = lastPage?.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL;
  const fields = pages.flatMap((page) => page.fields ?? []);

  return {
    title: form.title,
    ...(form.description ? { description: form.description } : {}),
    submitLabel,
    inputAppearance,
    ...(form.coverImageUrl ? { coverImageUrl: form.coverImageUrl } : {}),
    ...(themeColor ? { themeColor } : {}),
    pages: pages.map((page) => ({
      id: page.id,
      order: page.order,
      ...(page.title ? { title: page.title } : {}),
      ...(page.description ? { description: page.description } : {}),
      cta: {
        primaryLabel: page.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL,
        ...(page.cta?.backLabel?.trim() ? { backLabel: page.cta.backLabel.trim() } : {}),
      },
      fields: (page.fields ?? []).map(mapFormFieldToDto),
    })),
    fields: fields.map(mapFormFieldToDto),
  };

}

/** Flat DTO slice for AI: form chrome + one page's fields only. */
export function mapActivePageToAiDto(form: BuiltForm, page: FormPage): BuiltFormDto {
  const full = mapBuiltFormToDto(form);
  return {
    title: full.title,
    ...(full.description ? { description: full.description } : {}),
    submitLabel: page.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL,
    inputAppearance: full.inputAppearance,
    ...(full.coverImageUrl ? { coverImageUrl: full.coverImageUrl } : {}),
    ...(full.themeColor ? { themeColor: full.themeColor } : {}),
    fields: (page.fields ?? []).map(mapFormFieldToDto),
  };


}
